import { useState, useEffect, useCallback, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Info } from "lucide-react";
import { cn } from "@/utils";
import { format, addMonths } from "date-fns";
import { toast } from "sonner";
import { formatCurrency } from "@/utils";
import { convertToUTC2, formatDateDisplay, parseDisplayDate } from "@/utils/dateUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Constants for payment installment configuration
 */
const PAYMENT_INSTALLMENT_CONSTANTS = {
  MAX_INSTALLMENTS: 12,
  MIN_FUTURE_DAYS: 14,
  DECIMAL_PLACES: 2,
  DATE_FORMAT: 'dd/MM/yyyy',
} as const;

/**
 * Interface for payment installment data structure
 */
interface PaymentInstallment {
  date: string;
  amount: number;
}

/**
 * Props for the PaymentInstallments component
 * @interface PaymentInstallmentsProps
 */
interface PaymentInstallmentsProps {
  /** Current number of installments selected or "none" */
  numberOfInstallments: number | "none";
  
  /** Callback when installment count changes */
  onInstallmentsChange: (value: number | "none") => void;
  
  /** Array of payment installments with date and amount */
  paymentInstallments: PaymentInstallment[];
  
  /** Callback when individual installment date or amount changes */
  onPaymentInstallmentChange: (index: number, field: 'date' | 'amount', value: string | number) => void;
  
  /** Total purchase price to be divided */
  purchasePrice: number;
  
  /** Whether the component is in edit mode - disables automatic fractioning and date progression */
  isEditMode?: boolean;
  
  /** Callback to report validation errors */
  onValidationErrors?: (errors: string[]) => void;

  /** Maximum number of installments allowed (defaults to 12) */
  maxInstallments?: number;
}

export function PaymentInstallments({
  numberOfInstallments,
  onInstallmentsChange,
  paymentInstallments,
  onPaymentInstallmentChange,
  purchasePrice,
  isEditMode = false,
  onValidationErrors,
  maxInstallments = PAYMENT_INSTALLMENT_CONSTANTS.MAX_INSTALLMENTS
}: PaymentInstallmentsProps) {
  const [dateInputValues, setDateInputValues] = useState<string[]>(Array(6).fill(''));
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  /**
   * Calculates the minimum allowed date (2 weeks from today)
   * @returns Date object representing the minimum payment date
   */
  const getMinimumDate = useCallback((): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + PAYMENT_INSTALLMENT_CONSTANTS.MIN_FUTURE_DAYS);
    return minDate;
  }, []);

  /**
   * Fractions the purchase price across installments with precision handling
   * Implements mathematical rounding strategy where:
   * - Calculates base amount rounded down to 2 decimal places
   * - Allocates remainder to first installment to ensure exact total
   * 
   * @param numInstallments - Number of installments to divide into
   * @param amount - Total amount to divide
   * @returns Array of fractioned amounts
   */
  const calculateFractionedAmounts = useCallback((numInstallments: number, amount: number): number[] => {
    if (numInstallments <= 0 || amount <= 0) return [];

    const baseAmount = Math.floor((amount / numInstallments) * 100) / 100;
    const remainder = amount - (baseAmount * numInstallments);
    
    const amounts: number[] = [];
    for (let i = 0; i < numInstallments; i++) {
      if (i === 0) {
        amounts.push(Number((baseAmount + remainder).toFixed(PAYMENT_INSTALLMENT_CONSTANTS.DECIMAL_PLACES)));
      } else {
        amounts.push(baseAmount);
      }
    }
    return amounts;
  }, []);

  /**
   * Validates payment date is within acceptable range
   * @param date - Date to validate
   * @returns boolean indicating if date is valid
   */
  const isValidPaymentDate = useCallback((date: Date): boolean => {
    const minDate = getMinimumDate();
    return date >= minDate;
  }, [getMinimumDate]);

  /**
   * Updates subsequent payment dates automatically based on first payment date
   * Uses monthly progression: if first payment is 11/09/2026, next is 11/10/2026, etc.
   * IMPORTANT: Disabled in edit mode to prevent altering existing payment schedules
   * @param startDate - The first payment date
   * @param startIndex - Starting index (should be 0 for automatic progression)
   */
  const updateSubsequentDates = useCallback((startDate: Date, startIndex: number) => {
    // Only auto-populate if:
    // 1. This is the first payment date (index 0)
    // 2. Multiple installments exist
    // 3. NOT in edit mode (don't alter existing schedules)
    if (!isEditMode && startIndex === 0 && typeof numberOfInstallments === 'number' && numberOfInstallments > 1) {
      for (let i = 1; i < numberOfInstallments; i++) {
        const nextDate = addMonths(startDate, i);
        onPaymentInstallmentChange(i, 'date', convertToUTC2(nextDate));
        
        // Update display values
        setDateInputValues(prev => {
          const newValues = [...prev];
          newValues[i] = format(nextDate, PAYMENT_INSTALLMENT_CONSTANTS.DATE_FORMAT);
          return newValues;
        });
      }
    }
  }, [numberOfInstallments, onPaymentInstallmentChange, isEditMode]);

  /**
   * Handles installment count selection
   * Triggers automatic payment amount fractioning
   * IMPORTANT: Disabled in edit mode to prevent altering existing payment schedules
   * @param value - Selected installment count or "none"
   */
  const handleInstallmentsChange = useCallback((value: string) => {
    const errors: string[] = [];

    if (value === "none") {
      onInstallmentsChange("none");
      setValidationErrors([]);
    } else {
      const numInstallments = parseInt(value);
      
      // Validate installment count
      if (numInstallments > PAYMENT_INSTALLMENT_CONSTANTS.MAX_INSTALLMENTS) {
        const error = `Maximum ${PAYMENT_INSTALLMENT_CONSTANTS.MAX_INSTALLMENTS} installments allowed`;
        errors.push(error);
        setValidationErrors(errors);
        onValidationErrors?.(errors);
        toast.error(error);
        return;
      }

      onInstallmentsChange(numInstallments);
      
      // Automatically fraction the payment amount across installments
      // DISABLED IN EDIT MODE: Don't alter existing payment schedules
      if (!isEditMode && numInstallments > 0 && purchasePrice > 0) {
        const fractionedAmounts = calculateFractionedAmounts(numInstallments, purchasePrice);
        
        // Initialize installments with empty dates and calculated amounts
        // This ensures all indices get updated correctly
        for (let i = 0; i < numInstallments; i++) {
          onPaymentInstallmentChange(i, 'amount', fractionedAmounts[i]);
        }
      }

      setValidationErrors([]);
    }
  }, [onInstallmentsChange, onPaymentInstallmentChange, purchasePrice, calculateFractionedAmounts, onValidationErrors, isEditMode]);

  // ...existing code...

  // Initialize date input values from payment installments
  useEffect(() => {
    const newDateInputValues = paymentInstallments.map((installment) => {
      if (!installment || !installment.date) return '';
      return formatDateDisplay(installment.date);
    });

    setDateInputValues(prev => {
      // Only update if values are different to avoid infinite loop
      const currentValues = newDateInputValues.join(',');
      const prevValues = prev.slice(0, newDateInputValues.length).join(',');
      
      if (prevValues !== currentValues) {
        return [...newDateInputValues, ...Array(Math.max(0, 12 - newDateInputValues.length)).fill('')];
      }
      return prev;
    });
  }, [paymentInstallments]);

  /**
   * Handles manual date input with validation
   * Supports DD/MM/YYYY format and triggers automatic subsequent date population
   * @param index - Index of the installment being updated
   * @param inputValue - Raw date input string
   */
  const handleDateInput = useCallback((index: number, inputValue: string) => {
    setDateInputValues(prev => {
      const newValues = [...prev];
      newValues[index] = inputValue;
      return newValues;
    });

    if (!inputValue) {
      onPaymentInstallmentChange(index, 'date', '');
      return;
    }

    // Try parsing the date in DD/MM/YYYY format (UK format)
    const parsedDate = parseDisplayDate(inputValue);

    if (!parsedDate) {
      toast.error("Invalid date format. Please use DD/MM/YYYY");
      return;
    }

    if (!isValidPaymentDate(parsedDate)) {
      const minDate = getMinimumDate();
      const minDateStr = format(minDate, PAYMENT_INSTALLMENT_CONSTANTS.DATE_FORMAT);
      toast.error(`Payment date must be at least ${PAYMENT_INSTALLMENT_CONSTANTS.MIN_FUTURE_DAYS} days in the future (from ${minDateStr})`);
      return;
    }

    // Update the payment date
    onPaymentInstallmentChange(index, 'date', convertToUTC2(parsedDate));
    
    // If this is the first payment date, automatically set subsequent dates
    updateSubsequentDates(parsedDate, index);
  }, [onPaymentInstallmentChange, isValidPaymentDate, getMinimumDate, updateSubsequentDates]);

  /**
   * Handles calendar date selection with validation
   * @param index - Index of the installment being updated
   * @param date - Selected date from calendar
   */
  const handleCalendarSelect = useCallback((index: number, date: Date | null) => {
    if (!date) return;

    if (!isValidPaymentDate(date)) {
      const minDate = getMinimumDate();
      const minDateStr = format(minDate, PAYMENT_INSTALLMENT_CONSTANTS.DATE_FORMAT);
      toast.error(`Payment date must be at least ${PAYMENT_INSTALLMENT_CONSTANTS.MIN_FUTURE_DAYS} days in the future (from ${minDateStr})`);
      return;
    }

    // Update the payment date
    onPaymentInstallmentChange(index, 'date', convertToUTC2(date));
    
    // Update display value
    setDateInputValues(prev => {
      const newValues = [...prev];
      newValues[index] = format(date, PAYMENT_INSTALLMENT_CONSTANTS.DATE_FORMAT);
      return newValues;
    });
    
    // If this is the first payment date, automatically set subsequent dates
    updateSubsequentDates(date, index);
  }, [onPaymentInstallmentChange, isValidPaymentDate, getMinimumDate, updateSubsequentDates]);

  /**
   * Handles amount input change with validation
   * @param index - Index of the installment
   * @param value - New amount value
   */
  const handleAmountChange = useCallback((index: number, value: string) => {
    const amount = parseFloat(value) || 0;
    
    // Validate amount is non-negative
    if (amount < 0) {
      toast.error("Amount cannot be negative");
      return;
    }

    onPaymentInstallmentChange(index, 'amount', amount);
  }, [onPaymentInstallmentChange]);

  /**
   * Memoized validation status for payment amounts
   * Calculates sum of all installment amounts and compares to purchase price
   */
  const amountValidationStatus = useMemo(() => {
    // Calculate sum of all payment installments
    const calculatedSum = paymentInstallments.reduce((sum, installment) => {
      return sum + (Number(installment.amount) || 0);
    }, 0);
    
    const difference = purchasePrice - calculatedSum;
    return {
      isValid: calculatedSum >= purchasePrice,
      difference: Math.abs(difference),
      sum: calculatedSum,
      message: difference > 0 
        ? `Total allocated: ${formatCurrency(calculatedSum)} | You must allocate ${formatCurrency(difference)} more to meet the required amount of ${formatCurrency(purchasePrice)}.`
        : difference < 0
        ? `Total allocated: ${formatCurrency(calculatedSum)} | You have allocated ${formatCurrency(Math.abs(difference))} more than the required amount of ${formatCurrency(purchasePrice)}.`
        : `Total allocated: ${formatCurrency(calculatedSum)} | The total payment amount meets the required amount of ${formatCurrency(purchasePrice)}.`
    };
  }, [purchasePrice, paymentInstallments]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="installments-select">Number of Installments *</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>On creation does its best to help fraction and stage payment dates. Disabled once contract has been created</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select
          value={numberOfInstallments === "none" || numberOfInstallments === 0 ? "none" : numberOfInstallments.toString()}
          onValueChange={handleInstallmentsChange}
        >
          <SelectTrigger id="installments-select" aria-label="Select number of installments">
            <SelectValue placeholder="Select number of installments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Select Installments</SelectItem>
            {[...Array(maxInstallments).keys()].map((num) => (
              <SelectItem key={num + 1} value={(num + 1).toString()}>
                {num + 1} {num + 1 === 1 ? "installment" : "installments"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {typeof numberOfInstallments === 'number' && numberOfInstallments > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {Array(numberOfInstallments).fill(null).map((_, index) => (
            <div key={index} className="space-y-2">
              <div className="space-y-2">
                <Label className="text-xs" htmlFor={`payment-date-${index}`}>
                  Payment Date #{index + 1}
                </Label>
                <div className="flex">
                  <Input
                    id={`payment-date-${index}`}
                    placeholder={PAYMENT_INSTALLMENT_CONSTANTS.DATE_FORMAT}
                    value={dateInputValues[index] || ''}
                    onChange={(e) => handleDateInput(index, e.target.value)}
                    className="rounded-r-none text-xs h-8"
                    aria-label={`Payment date ${index + 1}`}
                    inputMode="text"
                  />
                  <Popover modal={false}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        type="button"
                        className={cn(
                          "rounded-l-none border-l-0 h-8 w-8 p-0",
                          !(paymentInstallments[index] && paymentInstallments[index]?.date) && "text-muted-foreground"
                        )}
                        aria-label={`Open calendar for payment date ${index + 1}`}
                      >
                        <CalendarIcon className="h-3 w-3" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={paymentInstallments[index] && paymentInstallments[index]?.date ? new Date(paymentInstallments[index]?.date) : undefined}
                        onSelect={(date) => handleCalendarSelect(index, date)}
                        fromDate={getMinimumDate()}
                        aria-label={`Calendar picker for payment date ${index + 1}`}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs" htmlFor={`payment-amount-${index}`}>
                  Amount #{index + 1}
                </Label>
                <Input
                  id={`payment-amount-${index}`}
                  type="number"
                  value={paymentInstallments[index] ? (paymentInstallments[index]?.amount || '') : ''}
                  onChange={(e) => handleAmountChange(index, e.target.value)}
                  placeholder="0.00"
                  className="text-xs h-8"
                  aria-label={`Payment amount ${index + 1}`}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {typeof numberOfInstallments === 'number' && numberOfInstallments > 0 && (
        <div 
          className="text-sm"
          role="status"
          aria-live="polite"
          aria-label="Payment amount validation status"
        >
          {!amountValidationStatus.isValid ? (
            <span className="text-destructive">
              {amountValidationStatus.message}
            </span>
          ) : (
            <span className="text-green-600">
              {amountValidationStatus.message}
            </span>
          )}
        </div>
      )}

      {validationErrors.length > 0 && (
        <div 
          className="rounded-md bg-red-50 p-4"
          role="alert"
          aria-label="Validation errors"
        >
          <ul className="list-disc list-inside space-y-1">
            {validationErrors.map((error, idx) => (
              <li key={idx} className="text-sm text-red-800">{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default PaymentInstallments;